import React, { useState, useEffect, useRef } from 'react';
import { 
    Search, Globe, ExternalLink, Loader2, Sparkles, 
    ShieldCheck, AlertTriangle, BookOpen, ChevronRight,
    SearchCheck, Info, FileText, Landmark, X, Copy,
    Check, ArrowUpRight, History, Bookmark, Compass,
    RefreshCw, Zap, Cpu, GraduationCap,
    Lock, ShieldAlert, Database
} from 'lucide-react';
import { searchClinicalRegistry } from '../services/geminiService';
import { AdUnit } from '../components/AdUnit';
import { useAuth } from '../components/AuthBridge';
import { SponsoredService } from '../services/sponsoredService';
import { useNavigate } from 'react-router';
import { ModuleHeader } from '../components/ModuleHeader';
import { useThemeTokens } from '../hooks/useActiveTheme';

const SUGGESTED_QUERIES = [
    "Texas 2024 CNA Scope of Practice",
    "California Nurse Aide Safety Criticals",
    "HIPAA Compliance for Long-Term Care",
    "Florida Board Abuse Reporting Rules",
    "CMS Hand Hygiene Guidelines 2024"
];

export const RegistrySearch: React.FC = () => {
    const { user, activeCert } = useAuth();
    const tokens = useThemeTokens();
    const navigate = useNavigate();
    const [config, setConfig] = useState(SponsoredService.getConfig());
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<{ text: string, sources: { uri: string, title: string }[] } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isCopied, setIsCopied] = useState(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const handleConfigUpdate = () => setConfig(SponsoredService.getConfig());
        window.addEventListener('sponsored-config-updated', handleConfigUpdate);
        SponsoredService.fetchLatestConfig().then(latest => setConfig(latest));
        
        const saved = localStorage.getItem('cna_recent_registry_searches');
        if (saved) setRecentSearches(JSON.parse(saved));
        
        if (user?.planTier === 'paid' && config.isRegistrySearchEnabled) {
            inputRef.current?.focus();
        }

        return () => window.removeEventListener('sponsored-config-updated', handleConfigUpdate);
    }, [user?.planTier, config.isRegistrySearchEnabled]);

    const handleSearch = async (searchQuery: string) => {
        const finalQuery = searchQuery.trim();
        if (!finalQuery || isLoading) return;

        setQuery(finalQuery);
        setIsLoading(true);
        setError(null);
        setResult(null);

        const nextHistory = [finalQuery, ...recentSearches.filter(s => s !== finalQuery)].slice(0, 5);
        setRecentSearches(nextHistory);
        localStorage.setItem('cna_recent_registry_searches', JSON.stringify(nextHistory));

        try {
            const data = await searchClinicalRegistry(finalQuery);
            setResult(data);
        } catch (err) {
            console.error(err);
            setError("The clinical logic bridge was interrupted. Verify your network partition.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        if (!result) return;
        navigator.clipboard.writeText(result.text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    if (user?.planTier !== 'paid') {
        return (
            <div className="max-w-4xl mx-auto py-10 md:py-20 animate-fade-in text-center">
                <ModuleHeader 
                    icon={Globe}
                    title="Board Rules Search"
                    description="Search state rules to find the latest requirements for your exam."
                    badge="Search Helper"
                    color="indigo"
                />
                <div className="bg-white dark:bg-slate-900 rounded-[3rem] p-6 sm:p-12 md:p-20 shadow-2xl border border-slate-200 dark:border-slate-800 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-3xl rounded-full"></div>
                    <div className="w-24 h-24 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded-[2rem] flex items-center justify-center mx-auto mb-10 shadow-xl border border-indigo-100 dark:border-indigo-900 group-hover:rotate-12 transition-transform duration-700">
                        <Lock size={48} />
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter leading-tight mb-6 text-slate-900 dark:text-slate-100">Elite Study Search</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-lg leading-relaxed italic max-w-xl mx-auto mb-12">
                        "Upgrade to Elite Pro to search through all state board rules, regulations, and official guides."
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button 
                            onClick={() => navigate(`/${activeCert?.id || 'cna'}/elite`)}
                            className={`px-12 py-5 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl transition-all active:scale-95 ${tokens.primaryBtn}`}
                        >
                            Upgrade to Elite Pro
                        </button>
                        <button 
                            onClick={() => navigate(-1)}
                            className="px-12 py-5 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-300 rounded-2xl font-black text-xs uppercase tracking-widest border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95"
                        >
                            Go Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto pb-32 animate-fade-in text-slate-900 dark:text-slate-100 space-y-10">
            <ModuleHeader 
                icon={Globe}
                title="Board Rules Search"
                description="Search state rules to find the latest requirements for your exam."
                badge="Search Helper"
                color="indigo"
            />
            
            <section className={`rounded-[2.5rem] md:rounded-[4rem] p-5 sm:p-8 md:p-20 text-white shadow-2xl relative overflow-hidden mb-10 border group ${
                tokens.isClinical
                    ? 'bg-gradient-to-br from-[#0B2942] via-[#0D3C5C] to-[#071F33] border-teal-500/20'
                    : tokens.isHighContrast
                    ? 'bg-black text-amber-400 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]'
                    : tokens.isMinimal
                    ? 'bg-slate-900 border-slate-800'
                    : tokens.isVibrant
                    ? 'bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-950 border-violet-800'
                    : 'bg-slate-950 border-white/5'
            }`}>
                <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.1)_0%,transparent_60%)]"></div>
                <div className="relative z-10 flex flex-col items-center text-center max-w-4xl mx-auto space-y-12">
                    <div className="space-y-4">
                        <h2 className={`text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9] ${tokens.isClinical ? 'font-serif' : ''}`}>Find State Rules</h2>
                        <p className="text-slate-400 font-medium italic text-lg md:text-2xl">"Search our database to find official steps and state exam rules."</p>
                    </div>

                    <div className="w-full flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1 group">
                            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors" size={28} />
                            <input 
                                ref={inputRef}
                                type="text" 
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && handleSearch(query)}
                                placeholder="Type a topic to search..."
                                className="w-full p-6 pl-16 bg-white/5 border border-white/10 rounded-[1.8rem] text-xl outline-none focus:bg-white/10 transition-all font-bold text-white placeholder:text-slate-700 shadow-inner"
                            />
                        </div>
                        <button 
                            onClick={() => handleSearch(query)}
                            disabled={isLoading || !query.trim()}
                            className={`px-12 py-6 text-white rounded-[1.8rem] font-black text-sm uppercase tracking-widest shadow-2xl transition-all disabled:opacity-50 active:scale-95 flex items-center justify-center gap-4 ${tokens.primaryBtn}`}
                        >
                            {isLoading ? <Loader2 className="animate-spin" size={24} /> : <Zap size={24} className="fill-current" />}
                            Execute
                        </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {SUGGESTED_QUERIES.map(q => (
                            <button 
                                key={q} 
                                onClick={() => handleSearch(q)}
                                className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-white hover:bg-white/10 transition-all"
                            >
                                {q}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {error && (
                <div className="p-8 bg-rose-50 border-2 border-rose-100 rounded-[2.5rem] flex items-center gap-6 text-rose-600 animate-pop shadow-xl">
                    <AlertTriangle size={32} />
                    <p className="font-black uppercase text-sm tracking-widest leading-relaxed">{error}</p>
                </div>
            )}

            {result && (
                <div className="bg-white dark:bg-slate-900 p-8 md:p-16 rounded-[3rem] border-2 border-slate-200 dark:border-slate-800 shadow-2xl space-y-12 animate-slide-up relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-10 opacity-5 pointer-events-none text-slate-400"><ShieldCheck size={160}/></div>
                    
                    <div className="space-y-8 relative z-10">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 rounded-2xl border-2 border-emerald-100 dark:border-emerald-900/50 shadow-sm">
                                    <SearchCheck size={32} />
                                </div>
                                <h3 className="text-2xl font-black uppercase tracking-tight text-slate-900 dark:text-slate-100">Grounded Logical Analysis</h3>
                            </div>
                            <button 
                                onClick={handleCopy}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${isCopied ? 'bg-emerald-500 text-white shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                            >
                                {isCopied ? <Check size={14}/> : <Copy size={14}/>}
                                {isCopied ? 'Copied' : 'Copy Text'}
                            </button>
                        </div>
                        <div className="prose prose-slate max-w-none">
                            <p className="text-xl md:text-3xl leading-relaxed text-slate-700 dark:text-slate-300 font-medium italic border-l-[12px] border-indigo-50 dark:border-indigo-900/50 pl-10">
                                "{result.text}"
                            </p>
                        </div>
                    </div>

                    {result.sources?.length > 0 && (
                        <div className="space-y-8 pt-12 border-t-2 border-slate-50 dark:border-slate-800 relative z-10">
                            <h4 className="text-[11px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-[0.4em] flex items-center gap-3">
                                <Database size={18} className={tokens.accentText} /> Grounding Registry Artifacts
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {result.sources.map((s, i) => (
                                    <a 
                                        key={i} 
                                        href={s.uri} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="p-8 bg-slate-50 dark:bg-slate-800/60 rounded-[2.2rem] border-2 border-slate-100 dark:border-slate-800 flex items-center justify-between hover:bg-white dark:hover:bg-slate-800 hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-2xl transition-all group"
                                    >
                                        <div className="flex items-center gap-5">
                                            <div className="p-3.5 bg-white dark:bg-slate-900 rounded-2xl shadow-md text-slate-300 dark:text-slate-600 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                                <FileText size={24} />
                                            </div>
                                            <span className="font-black text-sm uppercase tracking-tight text-slate-800 dark:text-slate-200">{s.title || "Reference Document"}</span>
                                        </div>
                                        <ExternalLink size={24} className="text-slate-200 dark:text-slate-700 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
